import { STEP, personalise, stepFor } from "./funnel";
import ChoiceCardsStep from "./steps/choice-cards-step";
import DataHubStep from "./steps/data-hub-step";
import EmailStep from "./steps/email-step";
import FocusStep from "./steps/focus-step";
import NameStep from "./steps/name-step";
import RoleStep from "./steps/role-step";
import StackStep from "./steps/stack-step";
import VisitorTypeStep from "./steps/visitor-type-step";
import WaitlistScreen from "./steps/waitlist-screen";
import { FunnelScreen } from "./screen";

import type { SavedAnswer } from "./funnel";
import type { Lead, SaveInput } from "./types";

// Which screen renders which question. One entry per step in funnel.ts, matched
// by key: there is no generic type -> component dispatch any more, because every
// screen on the walk is designed for its own question and a generic renderer
// hasn't drawn one of them for a long time.

// A step in funnel.ts that no case below draws. Not reachable today — every step
// has one — but a question added without a screen is a bug in this file, and
// losing someone's place is the only honest thing to do about it.
function Lost() {
  return (
    <FunnelScreen title="We&rsquo;ve lost your place">
      <p className="mt-4 text-body text-zinc-500">
        Reload the page and we&rsquo;ll put you back where you were.
      </p>
    </FunnelScreen>
  );
}

// A save the server refused. Shown OVER whatever screen is up rather than
// inside one, because the screen has already moved on: answers are written
// optimistically, so by the time a refusal lands the question that caused it is
// behind them. The two that reach a lead are the run being closed and the
// network being gone, and both are worth saying out loud rather than swallowing.
function SaveError({ message }: { message: string }) {
  return (
    <div
      role="alert"
      className="fixed inset-x-0 top-0 z-50 bg-red-600 px-4 py-2.5 text-center text-caption font-normal text-white"
    >
      {message}
    </div>
  );
}

export default function FunnelView({
  lead,
  answers,
  stepKey,
  busy,
  saveError,
  token,
  save,
  back,
  onConverted,
  reload,
  oauthError,
}: {
  lead: Lead | null;
  answers: readonly SavedAnswer[];
  stepKey: string;
  busy: boolean;
  saveError: string | null;
  token: string | null;
  save: (key: string, input: SaveInput) => void;
  back?: () => void;
  onConverted: () => Promise<void>;
  reload: () => Promise<void>;
  oauthError?: string | null;
}) {
  // A claimed spot outranks the pointer. Finalizing is what ends the run, so
  // once it has, this is the screen — on this visit and on every one after it,
  // whatever question the row was parked on when it happened.
  if (lead?.status === "FINALIZED") {
    return <WaitlistScreen lead={lead} token={token} reload={reload} />;
  }

  const banner = saveError ? <SaveError message={saveError} /> : null;

  const found = stepFor(stepKey);
  if (!found) return <Lost />;

  const step = personalise(found, answers);
  const answer = answers.find((a) => a.key === step.key);
  const onSave = (input: SaveInput) => save(step.key, input);
  const onSkip =
    step.required === false ? () => save(step.key, { values: [] }) : undefined;
  // Back only exists while the answers are still the browser's. Once the account
  // is minted the funnel goes forward.
  const onBack = lead?.converted ? undefined : back;

  const screen = () => {
    switch (step.key) {
      case STEP.NAME:
        return (
          <NameStep
            step={step}
            answer={answer}
            busy={busy}
            save={onSave}
            back={onBack}
          />
        );
      case STEP.VISITOR_TYPE:
        return (
          <VisitorTypeStep
            step={step}
            busy={busy}
            save={onSave}
            skip={onSkip}
            back={onBack}
          />
        );
      case STEP.ROLE_OTHER:
        return (
          <RoleStep
            step={step}
            answer={answer}
            busy={busy}
            save={onSave}
            back={onBack}
          />
        );
      case STEP.COMPANY_TYPE:
      case STEP.CLIENT_TYPES:
      case STEP.ROLE:
        return (
          <ChoiceCardsStep
            step={step}
            answer={answer}
            busy={busy}
            save={onSave}
            back={onBack}
          />
        );
      case STEP.FOCUS:
        return (
          <FocusStep
            step={step}
            answers={answers}
            busy={busy}
            save={onSave}
            back={onBack}
          />
        );
      case STEP.STACK:
        return (
          <StackStep
            step={step}
            answer={answer}
            busy={busy}
            save={onSave}
            skip={onSkip}
            back={onBack}
          />
        );
      case STEP.EMAIL:
        return (
          <EmailStep
            step={step}
            answer={answer}
            busy={busy}
            token={token}
            onConverted={onConverted}
            back={onBack}
            oauthError={oauthError}
          />
        );
      case STEP.DATA_HUB:
        return lead ? (
          <DataHubStep
            step={step}
            lead={lead}
            answers={answers}
            busy={busy}
            token={token}
            save={onSave}
            reload={reload}
          />
        ) : null;
      default:
        return <Lost />;
    }
  };

  return (
    <>
      {banner}
      {screen()}
    </>
  );
}
